/**
 * ClassifyDialog — the artifact **classify** primary action (coverage §2.1).
 *
 * Classification is the one artifact action with a real [EXISTS] backing: the
 * `confidentiality` and `crystallization_stage` columns ship today, so this
 * dialog issues a plain `PATCH /api/artifacts/:id` through the boxed
 * dataProvider (`useUpdate`) — no proposed endpoint required. A small modal
 * (one of the five sanctioned border reasons: an overlay) with two shadcn
 * Selects (design-system §3 — shadcn-only widgets, no hand-rolled controls).
 */
import * as React from "react";
import { useUpdate } from "@refinedev/core";
import {
  CONFIDENTIALITY,
  CRYSTALLIZATION_STAGES,
  type Artifact,
} from "@aos/shared";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { humanize } from "../projects/_form";

function ClassifySelect({
  id,
  label,
  value,
  onChange,
  options,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (next: string) => void;
  options: readonly string[];
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger id={id} aria-label={label}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((opt) => (
          <SelectItem key={opt} value={opt}>
            {humanize(opt)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function ClassifyDialog({
  artifact,
  open,
  onOpenChange,
}: {
  artifact: Artifact;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { mutate: updateArtifact, isLoading } = useUpdate();
  const [confidentiality, setConfidentiality] = React.useState<string>(
    artifact.confidentiality,
  );
  const [stage, setStage] = React.useState<string>(
    artifact.crystallization_stage,
  );

  // Re-sync when reopened against a (possibly) refreshed record.
  React.useEffect(() => {
    if (open) {
      setConfidentiality(artifact.confidentiality);
      setStage(artifact.crystallization_stage);
    }
  }, [open, artifact.confidentiality, artifact.crystallization_stage]);

  const submit = () => {
    updateArtifact(
      {
        resource: "artifacts",
        id: artifact.id,
        values: {
          confidentiality,
          crystallization_stage: stage,
        },
        successNotification: {
          type: "success",
          message: "Artifact classified",
        },
      },
      { onSuccess: () => onOpenChange(false) },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Classify artifact</DialogTitle>
          <DialogDescription>
            Set the confidentiality and crystallization stage.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="classify-confidentiality">Confidentiality</Label>
            <ClassifySelect
              id="classify-confidentiality"
              label="Confidentiality"
              value={confidentiality}
              onChange={setConfidentiality}
              options={CONFIDENTIALITY}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="classify-stage">Crystallization stage</Label>
            <ClassifySelect
              id="classify-stage"
              label="Crystallization stage"
              value={stage}
              onChange={setStage}
              options={CRYSTALLIZATION_STAGES}
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button onClick={submit} disabled={isLoading}>
            {isLoading ? "Saving…" : "Classify"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ClassifyDialog;
